import React, { useState, useMemo } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Alert,
  CircularProgress,
} from '@mui/material'
import { Warning as WarningIcon } from '@mui/icons-material'

import { useDeleteTheme } from './Hooks/UseDeleteTheme'
import type { Theme } from './ThemeKeyPhrasesModal/Types'
import { truncateText } from '../Helpers/ThemesHelper'

interface DeleteThemeModalProps {
  open: boolean
  theme: Theme
  onClose: () => void
}

const DeleteThemeModal: React.FC<DeleteThemeModalProps> = ({
  open,
  theme,
  onClose,
}) => {
  const { loading, deleteTheme } = useDeleteTheme()
  const [isDeleting, setIsDeleting] = useState(false)

  const keyPhrasesCount = useMemo(() => {
    return theme.attributes?.query?.attributes?.key_phrases?.length || 0
  }, [theme])

  const handleDelete = async () => {
    setIsDeleting(true)
    const success = await deleteTheme(theme)
    if (success) {
      onClose()
    }
    setIsDeleting(false)
  }

  const isLoading = loading.isLoading || isDeleting

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      disablePortal
      data-testid="delete-theme-modal"
    >
      <DialogTitle>Delete Theme</DialogTitle>

      <DialogContent dividers aria-describedby="delete-theme-warning">
        <Typography sx={{ mb: 2 }} tabIndex={0}>
          Are you sure you want to delete the theme{' '}
          <strong>{truncateText(theme.attributes.name, 25)}</strong> ?
        </Typography>

        <Alert
          severity="warning"
          icon={<WarningIcon color="warning" />}
          sx={{ mb: 2 }}
          tabIndex={0}
        >
          <Typography variant="body2" sx={{ fontWeight: '500' }}>
            This action cannot be undone. The theme will be permanently deleted.
          </Typography>
          <ul tabIndex={0}>
            <li>
              <Typography variant="body2">
                {keyPhrasesCount} key phrases will be preserved.
              </Typography>
            </li>
            <li>
              <Typography variant="body2">
                All associated theme data will be removed.
              </Typography>
            </li>
          </ul>
        </Alert>

        <Typography variant="body2" tabIndex={0}>
          Key phrases will remain available for use in other themes.
        </Typography>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={onClose}
          disabled={isLoading}
          color="inherit"
          data-testid="cancel-delete-button"
        >
          Cancel
        </Button>
        <Button
          onClick={handleDelete}
          disabled={isLoading}
          color="error"
          startIcon={
            isLoading ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default React.memo(DeleteThemeModal)
